import { useEffect, useRef, useState } from 'react';
import { Check, Loader2, Music, Pause, Play, Trash2, Upload, Volume2, VolumeX } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Slider } from '../../../components/ui/slider';
import { cn } from '../../../components/ui/utils';
import { musicApi, type TrackDTO } from '../../../shared/api/music.api';

export interface MusicSelection {
  url: string;
  volume: number; // 0-100
  fileName: string;
  startSec: number; // offset into the track where playback begins
}

interface Props {
  value: MusicSelection | null;
  onChange: (sel: MusicSelection | null) => void;
  /** Reel video length — the music window. null while still probing. */
  videoDurationSec: number | null;
}

const ACCEPT = '.mp3,.m4a,.aac';

function fmt(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function MusicSection({ value, onChange, videoDurationSec }: Props) {
  const [tracks, setTracks] = useState<TrackDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [trackDurationSec, setTrackDurationSec] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playingUrl, setPlayingUrl] = useState<string | null>(null);

  async function refresh() {
    setLoading(true);
    try {
      setTracks(await musicApi.list());
      setError(null);
    } catch {
      setError('Не удалось загрузить список треков');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void refresh(); }, []);

  // Probe the selected track's duration so the start slider can be bounded.
  useEffect(() => {
    setTrackDurationSec(null);
    if (!value?.url) return;
    const probe = new Audio(value.url);
    const onMeta = () => {
      if (isFinite(probe.duration) && probe.duration > 0) setTrackDurationSec(probe.duration);
    };
    probe.addEventListener('loadedmetadata', onMeta);
    return () => probe.removeEventListener('loadedmetadata', onMeta);
  }, [value?.url]);

  async function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const ok = ['.mp3', '.m4a', '.aac'].some((x) => file.name.toLowerCase().endsWith(x));
    if (!ok) { setError('Только mp3, m4a, aac'); return; }
    setLoading(true);
    try {
      const t = await musicApi.upload(file);
      setError(null);
      await refresh();
      onChange({ url: t.url, volume: value?.volume ?? 100, fileName: t.fileName, startSec: 0 });
    } catch {
      setError('Загрузка не удалась');
    } finally {
      setLoading(false);
    }
  }

  async function handleRemove(t: TrackDTO) {
    if (playingUrl === t.url) {
      audioRef.current?.pause();
      setPlayingUrl(null);
    }
    setLoading(true);
    try {
      await musicApi.remove(t.fileName);
      if (value?.fileName === t.fileName) onChange(null);
      await refresh();
    } catch {
      setError('Не удалось удалить трек');
    } finally {
      setLoading(false);
    }
  }

  function togglePlay(url: string) {
    if (playingUrl === url) {
      audioRef.current?.pause();
      setPlayingUrl(null);
      return;
    }
    audioRef.current?.pause();
    const a = new Audio(url);
    audioRef.current = a;
    // Preview the selected track from its chosen offset; other rows from 0.
    const startAt = value?.url === url ? value.startSec : 0;
    a.addEventListener('loadedmetadata', () => { a.currentTime = startAt; });
    a.onended = () => setPlayingUrl(null);
    a.onerror = () => setPlayingUrl(null);
    a.volume = (value?.volume ?? 100) / 100;
    void a.play().then(() => setPlayingUrl(url)).catch(() => setPlayingUrl(null));
  }

  useEffect(() => () => { audioRef.current?.pause(); }, []);

  const selectNone = value === null;
  const startMax = Math.max(0, (trackDurationSec ?? 0) - (videoDurationSec ?? 0));

  // If the window shrank below the chosen offset (e.g. a shorter video was
  // swapped in), clamp the STORED startSec too — otherwise the slider shows a
  // clamped thumb while a stale large value gets published (→ silent music).
  useEffect(() => {
    if (value && trackDurationSec !== null && videoDurationSec !== null && value.startSec > startMax) {
      onChange({ ...value, startSec: startMax });
    }
  }, [value, trackDurationSec, videoDurationSec, startMax, onChange]);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 text-sm font-medium">
          <Music className="h-4 w-4 text-muted-foreground" />
          Музыка
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={loading}
          onClick={() => fileInputRef.current?.click()}
        >
          {loading
            ? <Loader2 className="h-4 w-4 animate-spin" />
            : <Upload className="h-4 w-4" />}
          Загрузить трек
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPT}
          className="hidden"
          onChange={handleUpload}
        />
      </div>

      {error && <p className="text-xs text-destructive">{error}</p>}

      <div className="overflow-hidden rounded-md border divide-y">
        <button
          type="button"
          onClick={() => onChange(null)}
          className={cn(
            'flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm transition-colors',
            selectNone ? 'bg-accent' : 'hover:bg-accent/50',
          )}
        >
          <VolumeX className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="flex-1 truncate">Без музыки <span className="text-muted-foreground">(звук из видео)</span></span>
          {selectNone && <Check className="h-4 w-4 shrink-0 text-primary" />}
        </button>

        {tracks.map((t) => {
          const selected = value?.fileName === t.fileName;
          const isPlaying = playingUrl === t.url;
          return (
            <div
              key={t.fileName}
              role="button"
              tabIndex={0}
              onClick={() => onChange({ url: t.url, volume: value?.volume ?? 100, fileName: t.fileName, startSec: 0 })}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onChange({ url: t.url, volume: value?.volume ?? 100, fileName: t.fileName, startSec: 0 });
                }
              }}
              className={cn(
                'flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-sm transition-colors',
                selected ? 'bg-accent' : 'hover:bg-accent/50',
              )}
            >
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0"
                aria-label={isPlaying ? 'Пауза' : 'Прослушать'}
                onClick={(e) => { e.stopPropagation(); togglePlay(t.url); }}
              >
                {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
              </Button>
              <span className="flex-1 truncate" title={t.name}>{t.name}</span>
              {selected && <Check className="h-4 w-4 shrink-0 text-primary" />}
              <Button
                type="button"
                variant="ghost"
                size="icon"
                disabled={loading}
                aria-label={`Удалить ${t.name}`}
                className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                onClick={(e) => { e.stopPropagation(); void handleRemove(t); }}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          );
        })}

        {tracks.length === 0 && (
          <p className="px-3 py-3 text-center text-xs text-muted-foreground">
            {loading ? 'Загрузка…' : 'Пока нет загруженных треков'}
          </p>
        )}
      </div>

      {value && (
        <div className="space-y-2 rounded-md border bg-muted/30 px-3 py-2">
          <div className="flex items-center gap-3">
            <span className="w-16 shrink-0 text-xs text-muted-foreground">Начало</span>
            <Slider
              min={0}
              max={startMax}
              step={1}
              value={[Math.min(value.startSec, startMax)]}
              disabled={trackDurationSec === null || videoDurationSec === null || startMax === 0}
              onValueChange={([v]) => {
                onChange({ ...value, startSec: v });
                if (audioRef.current && playingUrl === value.url) {
                  audioRef.current.currentTime = v;
                }
              }}
              className="flex-1"
              aria-label="Начало трека"
            />
            <span className="w-12 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
              {fmt(value.startSec)}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Volume2 className="h-4 w-4 shrink-0 text-muted-foreground" />
            <Slider
              min={0}
              max={100}
              step={1}
              value={[value.volume]}
              onValueChange={([v]) => {
                onChange({ ...value, volume: v });
                if (audioRef.current && playingUrl === value.url) {
                  audioRef.current.volume = v / 100;
                }
              }}
              className="flex-1"
              aria-label="Громкость музыки"
            />
            <span className="w-10 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
              {value.volume}%
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
